/**
 * Guided-flow primitives shared by App surfaces: the numbered step rail and
 * the guide-moment card (recommended default + one-sentence why — accepting
 * the default is success; C Mode rule 3 / MR-3).
 */

import type { ReactNode } from 'react';
import { AlertTriangle, Check, ChevronRight } from 'lucide-react';
import { AppCard, Eyebrow, PrimaryButton, SecondaryButton } from './primitives';

export type StepStatus = 'done' | 'current' | 'todo' | 'warn';

export interface FlowStep {
  key: string;
  label: string;
  /** One plain-language sentence about what this step settles. */
  detail?: string;
  status: StepStatus;
}

/** Vertical numbered step rail. Each step names its state at a glance;
 *  clicking navigates when `onSelect` is provided. */
export function StepRail({
  steps,
  onSelect,
}: {
  steps: FlowStep[];
  onSelect?: (key: string) => void;
}) {
  return (
    <ol className="space-y-2">
      {steps.map((step, i) => {
        const marker =
          step.status === 'done' ? (
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-alpine-green-100 text-alpine-green-700 dark:bg-alpine-green-900/40 dark:text-alpine-green-300">
              <Check className="h-4 w-4" />
            </span>
          ) : step.status === 'warn' ? (
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-electric-mustard-100 text-electric-mustard-800 dark:bg-electric-mustard-900/40 dark:text-electric-mustard-300">
              <AlertTriangle className="h-4 w-4" />
            </span>
          ) : (
            <span
              className={`flex h-7 w-7 items-center justify-center rounded-full font-mono text-xs ${
                step.status === 'current'
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 text-gray-500 dark:bg-[var(--surface-raised)] dark:text-[var(--text-sec)]'
              }`}
            >
              {i + 1}
            </span>
          );

        const inner = (
          <div className="flex items-center gap-3">
            {marker}
            <div className="min-w-0 flex-1">
              <p
                className={`font-body text-sm ${
                  step.status === 'current'
                    ? 'font-medium text-gray-900 dark:text-[var(--text-pri)]'
                    : 'text-gray-700 dark:text-[var(--text-sec)]'
                }`}
              >
                {step.label}
              </p>
              {step.detail ? (
                <p className="truncate font-body text-xs text-gray-500 dark:text-[var(--text-ter)]">
                  {step.detail}
                </p>
              ) : null}
            </div>
            {onSelect ? <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" /> : null}
          </div>
        );

        return (
          <li key={step.key}>
            {onSelect ? (
              <button
                type="button"
                onClick={() => onSelect(step.key)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-left hover:border-gray-300 dark:border-[var(--rule)] dark:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]"
              >
                {inner}
              </button>
            ) : (
              <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-[var(--rule)] dark:bg-[var(--surface)]">
                {inner}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  );
}

/** A configuration moment the app cannot pre-decide: recommend a default,
 *  explain it in one sentence, and make accepting it the easy path. */
export function GuideMomentCard({
  question,
  recommendation,
  why,
  acceptLabel = 'Use the recommendation',
  onAccept,
  onAdjust,
  adjustLabel = 'Adjust',
  children,
}: {
  question: string;
  recommendation: string;
  why: string;
  acceptLabel?: string;
  onAccept: () => void;
  onAdjust?: () => void;
  adjustLabel?: string;
  /** Optional custom control revealed for adjusting. */
  children?: ReactNode;
}) {
  return (
    <AppCard>
      <Eyebrow>Guide moment</Eyebrow>
      <p className="mt-2 font-heading font-semibold text-gray-900 dark:text-[var(--text-pri)]">
        {question}
      </p>
      <p className="mt-1 font-body text-sm text-gray-700 dark:text-[var(--text-sec)]">
        <span className="font-medium text-gray-900 dark:text-[var(--text-pri)]">
          Recommended: {recommendation}.
        </span>{' '}
        {why}
      </p>
      {children}
      <div className="mt-4 flex flex-wrap gap-2">
        <PrimaryButton onClick={onAccept}>{acceptLabel}</PrimaryButton>
        {onAdjust ? <SecondaryButton onClick={onAdjust}>{adjustLabel}</SecondaryButton> : null}
      </div>
    </AppCard>
  );
}
